import { Skeleton, Stack } from "@mui/material";
import { useMemo } from "react";
import { useGetOneTimeProductsBySlugQuery } from "src/api/services/productsApi";
import { usePatientSubscriptions } from "src/context/PatientSubscriptionsProvider";
import ProductsList from "./components/ProductsList";
import { useLatestPrescription } from "src/context/LatestPrescriptionProvider";
import { useGetLatestOrderQuery } from "src/api/services/ordersApi";
import AdditionalProductsCollapse from "./components/AdditionalProductsCollapse";
import GLP1NextSteps from "src/components/GLP1NextSteps";
import { useRecommendedDosage } from "src/context/RecommendedDosageProvider";
import { useLocation } from "react-router-dom";
import { Paths } from "src/constants/paths";
import WhiteCard from "src/shared/components/WhiteCard";
import { ProductCategories } from "src/api/types";

const OneTimeProducts = () => {
  const subscriptions = usePatientSubscriptions((sub) => {
    const weightLossSub = sub.find(
      (s) => s.product_category.name === ProductCategories.WeightLoss,
    );

    if (weightLossSub == null) return sub;

    if (weightLossSub.status !== "canceled") return null;

    return [weightLossSub];
  });
  const pendingPrescription = useLatestPrescription((p) => {
    const weightLoss = p.product_category.name === ProductCategories.WeightLoss;

    return weightLoss && p.status === "created";
  });
  const recommended = useRecommendedDosage();
  const { data } = useGetLatestOrderQuery({
    billing_model: subscriptions?.[0]?.product?.billing_model,
    category: "Weight Loss",
  });
  const { pathname } = useLocation();

  const productSlug = useMemo(() => {
    if (data == null) return "st-ot";

    if (data.product.slug === "st") return "st-ot";

    if (data.product.slug === "tr") return "tr-ot";

    if (["iwl-ot", "wp-ot", "db-ot"].includes(data.product.slug))
      return "st-ot";

    return data.product.slug;
  }, [data]);

  const { data: monthly, isLoading: monthlyLoading } =
    useGetOneTimeProductsBySlugQuery({
      slug: productSlug,
      number_of_months: 1,
    });

  const { data: quarterly, isLoading: quarterlyLoading } =
    useGetOneTimeProductsBySlugQuery({
      slug: productSlug,
      number_of_months: 3,
    });

  const monthlyProducts = useMemo(() => {
    if (monthly == null || monthly.length === 0) return [];

    return monthly;
  }, [monthly]);

  const quarterlyProducts = useMemo(() => {
    if (quarterly == null || quarterly.length === 0) return [];

    return quarterly;
  }, [quarterly]);

  const hideProducts = useMemo(
    () =>
      subscriptions == null ||
      (monthlyProducts.length === 0 && quarterlyProducts.length === 0) ||
      pendingPrescription,
    [
      monthlyProducts.length,
      pendingPrescription,
      quarterlyProducts.length,
      subscriptions,
    ],
  );

  const hasRecommendedMonthly = useMemo(() => {
    if (
      recommended?.price.number_of_months ===
      monthly?.[0].price.number_of_months
    ) {
      return true;
    }

    return false;
  }, [monthly, recommended?.price.number_of_months]);

  const hasRecommendedQuarterly = useMemo(() => {
    if (
      recommended?.price.number_of_months ===
      quarterly?.[0].price.number_of_months
    ) {
      return true;
    }

    return false;
  }, [quarterly, recommended?.price.number_of_months]);

  return quarterlyLoading && monthlyLoading ? (
    <WhiteCard width={"100%"} height={600}>
      <Skeleton variant="rectangular" height="100%" width="100%" />
    </WhiteCard>
  ) : hideProducts ? (
    pathname.includes(Paths.SHOP) ? null : (
      <GLP1NextSteps />
    )
  ) : (
    <Stack gap={2}>
      <ProductsList
        products={quarterlyProducts}
        productSlug={productSlug}
        numberOfMonths={3}
        order={hasRecommendedQuarterly ? 0 : 1}
      />
      <ProductsList
        products={monthlyProducts}
        productSlug={productSlug}
        numberOfMonths={1}
        order={hasRecommendedMonthly ? 0 : 1}
      />
      <AdditionalProductsCollapse latestOrder={data} />
    </Stack>
  );
};

export default OneTimeProducts;
